import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Globe, Settings, Check, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface GlobalQuestionManagementProps {
  accessToken: string;
  currentUser: any;
}

interface Question {
  id: string;
  questionText: string;
  questionType: 'text' | 'textarea' | 'boolean' | 'multiple_choice' | 'checkbox' | 'select';
  options?: string[];
  placeholder?: string;
  helpText?: string;
  globalQuestion?: boolean;
  order?: number;
}

const CATEGORIES = [
  { id: 'assets', name: 'Asset Management' },
  { id: 'incidents', name: 'Incident Management' },
  { id: 'service_requests', name: 'Service Requests' },
  { id: 'problems', name: 'Problem Management' },
  { id: 'changes', name: 'Change Management' },
  { id: 'knowledge', name: 'Knowledge Management' },
];

export function GlobalQuestionManagement({
  accessToken,
  currentUser,
}: GlobalQuestionManagementProps) {
  const [selectedCategory, setSelectedCategory] = useState('assets');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
    questionType: 'text',
    options: [],
  });

  useEffect(() => {
    loadQuestions();
  }, [selectedCategory]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/global-questions/${selectedCategory}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load global questions');
      }

      const globalQuestions = await response.json();
      setQuestions(globalQuestions);
    } catch (error) {
      console.error('[GLOBAL_QUESTION_MGMT] Error loading:', error);
      toast.error('Failed to load global questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.questionText || !newQuestion.questionType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/global-questions/${selectedCategory}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...newQuestion,
            id: `global_${Date.now()}`,
            globalQuestion: true,
            order: questions.length,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add global question');
      }

      toast.success('Global question added successfully');
      setShowAddDialog(false);
      setNewQuestion({ questionType: 'text', options: [] });
      await loadQuestions();
    } catch (error) {
      console.error('[GLOBAL_QUESTION_MGMT] Error adding question:', error);
      toast.error('Failed to add global question');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuestion = async (question: Question) => {
    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/global-questions/${selectedCategory}/${question.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(question),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update global question');
      }

      toast.success('Global question updated successfully');
      setEditingQuestion(null);
      await loadQuestions();
    } catch (error) {
      console.error('[GLOBAL_QUESTION_MGMT] Error updating question:', error);
      toast.error('Failed to update global question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this global question? All firms will lose access to it.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/global-questions/${selectedCategory}/${questionId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete global question');
      }

      toast.success('Global question deleted successfully');
      await loadQuestions();
    } catch (error) {
      console.error('[GLOBAL_QUESTION_MGMT] Error deleting question:', error);
      toast.error('Failed to delete global question');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-6 h-6 text-purple-600" />
            <h2 className="text-gray-900">Global Question Library</h2>
          </div>
          <p className="text-gray-600 text-sm">
            Manage questions that all consulting firms can use in their discovery process
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Global Question
        </Button>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-6">
        <TabsList className="grid grid-cols-6 w-full">
          {CATEGORIES.map((cat) => (
            <TabsTrigger key={cat.id} value={cat.id}>
              {cat.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading questions...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No global questions yet for this category</p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Question
                </Button>
              </CardContent>
            </Card>
          ) : (
            questions.map((question, index) => (
              <Card key={question.id} className="border-purple-200 bg-gradient-to-r from-purple-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2 text-purple-600">
                      <Globe className="w-4 h-4" />
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{question.questionText}</p>
                            <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                              Global
                            </Badge>
                          </div>
                          {question.helpText && (
                            <p className="text-sm text-gray-600 mb-2">{question.helpText}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <Badge variant="secondary">{question.questionType}</Badge>
                            {question.options && question.options.length > 0 && (
                              <span>{question.options.length} options</span>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingQuestion(question)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-600" />
              Add Global Question
            </DialogTitle>
            <DialogDescription>
              Create a question that all consulting firms can use for{' '}
              {CATEGORIES.find((c) => c.id === selectedCategory)?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question Text *</Label>
              <Input
                value={newQuestion.questionText || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, questionText: e.target.value })}
                placeholder="e.g., How many users will need access?"
              />
            </div>

            <div className="space-y-2">
              <Label>Question Type *</Label>
              <Select
                value={newQuestion.questionType}
                onValueChange={(value: any) => setNewQuestion({ ...newQuestion, questionType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Short Text</SelectItem>
                  <SelectItem value="textarea">Long Text</SelectItem>
                  <SelectItem value="boolean">Yes/No</SelectItem>
                  <SelectItem value="multiple_choice">Multiple Choice (single)</SelectItem>
                  <SelectItem value="checkbox">Checkboxes (multiple)</SelectItem>
                  <SelectItem value="select">Dropdown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {['multiple_choice', 'checkbox', 'select'].includes(newQuestion.questionType || '') && (
              <div className="space-y-2">
                <Label>Options (one per line)</Label>
                <Textarea
                  rows={4}
                  value={newQuestion.options?.join('\n') || ''}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: e.target.value.split('\n').filter((o) => o.trim()),
                    })
                  }
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Help Text</Label>
              <Textarea
                rows={2}
                value={newQuestion.helpText || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, helpText: e.target.value })}
                placeholder="Additional context or explanation for this question"
              />
            </div>

            <div className="space-y-2">
              <Label>Placeholder (optional)</Label>
              <Input
                value={newQuestion.placeholder || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, placeholder: e.target.value })}
                placeholder="Placeholder text for text inputs"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddQuestion}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Adding...' : 'Add Question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={!!editingQuestion} onOpenChange={(open) => !open && setEditingQuestion(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-600" />
              Edit Global Question
            </DialogTitle>
            <DialogDescription>Update the question details</DialogDescription>
          </DialogHeader>

          {editingQuestion && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question Text *</Label>
                <Input
                  value={editingQuestion.questionText}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, questionText: e.target.value })
                  }
                />
              </div>

              {['multiple_choice', 'checkbox', 'select'].includes(editingQuestion.questionType) && (
                <div className="space-y-2">
                  <Label>Options (one per line)</Label>
                  <Textarea
                    rows={4}
                    value={editingQuestion.options?.join('\n') || ''}
                    onChange={(e) =>
                      setEditingQuestion({
                        ...editingQuestion,
                        options: e.target.value.split('\n').filter((o) => o.trim()),
                      })
                    }
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Help Text</Label>
                <Textarea
                  rows={2}
                  value={editingQuestion.helpText || ''}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, helpText: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingQuestion(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => editingQuestion && handleUpdateQuestion(editingQuestion)}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}